import { Routes, Route, Navigate } from 'react-router-dom';
import { useState, useEffect, createContext, useContext } from 'react';
import LoginPage from './pages/LoginPage';
import VerifyPage from './pages/VerifyPage';
import ImpersonatePage from './pages/ImpersonatePage';
import DashboardPage from './pages/DashboardPage';
import PreviewPage from './pages/PreviewPage';
import RevisionsPage from './pages/RevisionsPage';
import CustomerEditorPage from './pages/CustomerEditorPage';
import {
  getSession,
  getStoredSession,
  getStoredCustomer,
  clearSession,
  CustomerInfo,
} from './api/auth';

// Auth context
interface AuthContextType {
  customer: CustomerInfo | null;
  isLoading: boolean;
  setCustomer: (customer: CustomerInfo | null) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  customer: null,
  isLoading: true,
  setCustomer: () => {},
  logout: () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { customer, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!customer) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  const [customer, setCustomer] = useState<CustomerInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function validateSession() {
      const storedSession = getStoredSession();
      const storedCustomer = getStoredCustomer();

      if (!storedSession || !storedCustomer) {
        setIsLoading(false);
        return;
      }

      try {
        // Validate session with server
        const { customer: serverCustomer } = await getSession();
        setCustomer(serverCustomer);
      } catch {
        // Session invalid, clear it
        clearSession();
      } finally {
        setIsLoading(false);
      }
    }

    validateSession();
  }, []);

  const handleLogout = () => {
    clearSession();
    setCustomer(null);
  };

  return (
    <AuthContext.Provider
      value={{
        customer,
        isLoading,
        setCustomer,
        logout: handleLogout,
      }}
    >
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/verify" element={<VerifyPage />} />
        <Route path="/auth/impersonate" element={<ImpersonatePage />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/preview"
          element={
            <ProtectedRoute>
              <PreviewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/revisions"
          element={
            <ProtectedRoute>
              <RevisionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit"
          element={
            <ProtectedRoute>
              <CustomerEditorPage />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthContext.Provider>
  );
}
